// Game rules shared by the UI and the regression tests. Distances are in metres.
export const INTERACTION_DISTANCE = 150;
export const isPublicTransport = mode => ['train', 'bus', 'boat'].includes(mode);
export const moneyInCents = value => Math.round(Number(value) * 100);
export const roundMoney = value => moneyInCents(value) / 100;

export function shouldShowStation(station, settings) {
  switch (Number(station.type)) {
    case 1: return settings.showTrainStations;
    case 2:
    case 4: return settings.showBusStations;
    case 5: return settings.showFerryStations;
    case 9: return settings.showTrainStations || settings.showBusStations;
    default: return true;
  }
}

export function distanceBetween(a, b) {
  const radians = degrees => degrees * Math.PI / 180;
  const dLat = radians(b[1] - a[1]);
  const dLng = radians(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(radians(a[1])) *
    Math.cos(radians(b[1])) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1, h)));
}

export function validRoute(route) {
  const coordinates = route?.geometry?.coordinates;
  return route?.geometry?.type === 'LineString' && Array.isArray(coordinates) &&
    coordinates.length >= 2 && coordinates.every(c => Array.isArray(c) &&
      c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]) &&
      Math.abs(c[0]) <= 180 && Math.abs(c[1]) <= 90) &&
    coordinates.some(c => distanceBetween(coordinates[0], c) > 0);
}

export function departureTime(gameTime, offset, departureSeconds, delayDays = 0) {
  // The timetable's day is the player's local day, not the race's original zone.
  return Math.floor((gameTime + offset) / 86400) * 86400 +
    Number(delayDays) * 86400 + departureSeconds - offset;
}

export function routeTiming(route, duration, stops = []) {
  if (!validRoute(route) || !Number.isFinite(Number(duration)) || Number(duration) <= 0) {
    throw new Error('This journey has no usable route or travel time.');
  }
  // Older route endpoints duplicate the first coordinate. Remove zero-length legs.
  const coordinates = route.geometry.coordinates.filter((c, i, all) =>
    i === 0 || distanceBetween(all[i - 1], c) > 0);
  const distances = coordinates.slice(1).map((c, i) => distanceBetween(coordinates[i], c));
  const anchors = [{ index: 0, time: 0 }];
  for (const stop of stops) {
    const time = Number(stop.duration_seconds);
    const previous = anchors[anchors.length - 1];
    if (!Number.isFinite(time) || time <= previous.time || time >= Number(duration)) continue;
    const index = coordinates.findIndex((c, i) => i > previous.index &&
      distanceBetween(c, [Number(stop.lng), Number(stop.lat)]) < 1);
    if (index > previous.index && index < coordinates.length - 1) anchors.push({ index, time });
  }
  anchors.push({ index: coordinates.length - 1, time: Number(duration) });
  const segmentTimes = [];
  for (let a = 1; a < anchors.length; a++) {
    const previous = anchors[a - 1];
    const next = anchors[a];
    const length = distances.slice(previous.index, next.index).reduce((sum, d) => sum + d, 0);
    for (let i = previous.index; i < next.index; i++) {
      segmentTimes[i] = distances[i] / length * (next.time - previous.time);
    }
  }
  return { coordinates, segmentTimes };
}

export function routePosition(coordinates, segmentTimes, elapsed) {
  let segment = 0;
  let remaining = Math.max(0, elapsed);
  while (segment < segmentTimes.length - 1 && remaining >= segmentTimes[segment]) {
    remaining -= segmentTimes[segment++];
  }
  const progress = Math.min(1, remaining / segmentTimes[segment]);
  const start = coordinates[segment];
  const end = coordinates[segment + 1];
  return {
    segment, progress,
    coordinate: start.map((n, i) => n + (end[i] - n) * progress),
    complete: segment === segmentTimes.length - 1 && progress >= 1,
  };
}
